import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  addSurplusResources41_1bLCA,
  deleteSurplusResources41_1bLCA,
  getAllSurplusResources41_1bLCA,
  updateSurplusResources41_1bLCA,
} from "../../../../../../apis/Product/UseStageFlow41Api";
import { getProductSurplusResourcesDisposal34_3Dropdown } from "../../../../../../apis/Dropdown";
import { REFRESH_USE_STAGE_41_1_TOTALS } from "../../Header";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const SurplusResources41_1bTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [disposalMethodDropdown, setDisposalMethodDropdown] = useState([]);
  const [unitDisposalMethods, setUnitDisposalMethods] = useState({});

  // Fetch disposal method dropdown (Flow 34.3)
  useEffect(() => {
    getProductSurplusResourcesDisposal34_3Dropdown({
      setDropdown: setDisposalMethodDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllSurplusResources41_1bLCA({
      productId,
      setObjects: (rows) => {
        setData(rows);
        window.dispatchEvent(new Event(REFRESH_USE_STAGE_41_1_TOTALS));
      },
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && disposalMethodDropdown.length > 0) {
      const newUnits = {};
      data.forEach((row) => {
        const disposalCode =
          typeof row.surplus_materials_waste_water === "string"
            ? row.surplus_materials_waste_water
            : row.surplus_materials_waste_water?.value || row.surplus_materials_waste_water?.code;
        if (disposalCode) {
          const selected = disposalMethodDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === disposalCode || opt.value === disposalCode)
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
          }
        }
      });
      setUnitDisposalMethods(newUnits);
    }
  }, [data, disposalMethodDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      surplus_materials_waste_water: {
        targetField: "quantity_per_single_use",
        getPlaceholder: (selectedValue, dropdownData, _rowData, rowId = "new") => {
          const selected = dropdownData?.find(
            (opt) => opt?.value === selectedValue || opt?.code === selectedValue
          );
          if (selected) {
            setUnitDisposalMethods((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "surplus_materials_waste_water",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const disposalCode =
        typeof editedItem.surplus_materials_waste_water === "string"
          ? editedItem.surplus_materials_waste_water
          : editedItem.surplus_materials_waste_water?.value || editedItem.surplus_materials_waste_water?.code || null;
      updateSurplusResources41_1bLCA({
        productId,
        rowId: editedItem.id,
        process_name: editedItem.process_name || null,
        surplus_materials_waste_water: disposalCode,
        quantity_per_single_use: editedItem.quantity_per_single_use || 0,
        cycles_per_lifetime: editedItem.cycles_per_lifetime || 0,
        objects: data,
        setObjects: setData,
        use_swal: true,
        callback: () =>
          window.dispatchEvent(new Event(REFRESH_USE_STAGE_41_1_TOTALS)),
      });
    },
    [productId, data]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const disposalCode =
        typeof newItem.surplus_materials_waste_water === "string"
          ? newItem.surplus_materials_waste_water
          : newItem.surplus_materials_waste_water?.value || newItem.surplus_materials_waste_water?.code || null;
      addSurplusResources41_1bLCA({
        productId,
        process_name: newItem.process_name || null,
        surplus_materials_waste_water: disposalCode,
        quantity_per_single_use: newItem.quantity_per_single_use || 0,
        cycles_per_lifetime: newItem.cycles_per_lifetime || 0,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteSurplusResources41_1bLCA({
        productId,
        rowId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "process_name",
        accessor: "process_name",
        header: `${t("PROCESS_NAME") || "Process name"} *`,
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: "surplus_materials_waste_water",
        accessor: "surplus_materials_waste_water",
        header: `${t("SURPLUS_MATERIALS_WASTE_WATER") || "Surplus materials or waste water"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_DISPOSAL_METHOD") || "Select disposal method...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "surplus_materials_waste_water",
            disposalMethodDropdown,
            unitDisposalMethods,
            rowId
          ),
      },
      {
        id: "quantity_per_single_use",
        accessor: "quantity_per_single_use",
        header: `${t("QUANTITY_PER_SINGLE_USE") || "Quantity per single use per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "surplus_materials_waste_water",
            disposalMethodDropdown,
            unitDisposalMethods,
            rowId
          ),
      },
      {
        id: "cycles_per_lifetime",
        accessor: "cycles_per_lifetime",
        header: `${t("CYCLES_PER_LIFETIME") || "Cycles (Uses) per functional unit during lifetime"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitDisposalMethods,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      surplus_materials_waste_water: disposalMethodDropdown || [],
    }),
    [disposalMethodDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({ [impactCategoryAccessor]: CustomInputComponent }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default SurplusResources41_1bTable;

